import { Component, ElementRef, HostListener, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, computed, signal, input, output } from '@angular/core'
import { form } from '@angular/forms/signals'
import { PageEvent } from '@angular/material/paginator'
import { AsiakkaanTuote, LaskuTuotteetHakuPyynto, LaskuTuotteetHakuVastaus, LaskunAlv, LaskunTyypit } from '@lemon/core-firebase/model/lasku'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { EMPTY, Subject, from } from 'rxjs'
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators'
import { FirebaseLemonaid } from '../../../_angular/service/firebase-lemonaid.service'
import { arvonMuutokset, asetaArvo } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LaskutusHakuStateService } from '../../../_jaettu-angular/laskut/haku/laskutus-haku-state.service'
import { TuoteDialogiService } from '../tuote-dialogi.service'
import { AlvService } from '../../../_jaettu-angular/service/alv.service'

interface NaytettavaHakutulos extends AsiakkaanTuote {
  alvProsentti: number | null
  hintaVerollinen: number | null
}

@Component({
  selector: 'app-tuoterekisteri-haku',
  templateUrl: './tuoterekisteri-haku.component.html',
  styleUrls: ['./tuoterekisteri-haku.component.css'],
  standalone: false,
})
export class TuoterekisteriHakuComponent implements OnInit, OnChanges, OnDestroy {
  readonly avoinna = input(false)
  readonly suljettu = output<void>()

  @ViewChild('hakukentta', { read: ElementRef }) set hakukentta(kentta: ElementRef<HTMLInputElement> | undefined) {
    if (kentta) {
      setTimeout(() => kentta.nativeElement.focus())
    }
  }

  hakuteksti = form(signal(''))
  readonly hakusana = computed(() => this.hakuteksti().value())
  readonly tulokset = signal<NaytettavaHakutulos[]>([])
  readonly haetaan = signal(false)
  readonly kokonaismaara = signal(0)
  sivu = 0
  sivunKoko = 50
  readonly sivukoot = [50, 100, 200]

  private _hakuTrigger = new Subject<void>()
  private _edellinenHakuJson: string | null = null
  private _ngUnsubscribe = new Subject<void>()
  private _kotimaanAlvt: LaskunAlv[] = []

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _hakuStateService: LaskutusHakuStateService,
    private _tuoteDialogiService: TuoteDialogiService,
    private _alvService: AlvService,
    private _currencyService: CurrencyService,
  ) {
    arvonMuutokset(this.hakuteksti).subscribe(() => this.hakutekstiMuuttui())
  }

  async ngOnInit() {
    const tallennettuHakutila = this._hakuStateService.tuote.palautaHakutila()
    if (tallennettuHakutila) {
      asetaArvo(this.hakuteksti, tallennettuHakutila.hakuteksti, { ilmoita: false })
      this.sivu = tallennettuHakutila.sivu
      this.sivunKoko = tallennettuHakutila.sivunKoko
    }

    this._kotimaanAlvt = await this._alvService.annaLaskutyypinAlvt(LaskunTyypit.TAVALLINEN, null)

    this._hakuTrigger
      .pipe(
        debounceTime(500),
        switchMap(() => {
          const hakuehdotPuuttuvat = !this.hakuteksti().value().trim()
          if (hakuehdotPuuttuvat) {
            this.haetaan.set(false)
            this.tulokset.set([])
            this.kokonaismaara.set(0)
            this._edellinenHakuJson = null
            return EMPTY
          }
          const pyynto = this._rakennaHakupyynto()
          const pyyntoJson = JSON.stringify(pyynto)
          if (pyyntoJson === this._edellinenHakuJson) {
            this.haetaan.set(false)
            return EMPTY
          }
          return from(this._suoritaHaku(pyynto)).pipe(map(vastaus => ({ vastaus, pyyntoJson })))
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(({ vastaus, pyyntoJson }) => this._kasitteleHakuvastaus(vastaus, pyyntoJson))

    if (tallennettuHakutila) {
      this._hakuTrigger.next()
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.avoinna) {
      return
    }
    if (changes.avoinna.currentValue) {
      this._hakuTrigger.next()
    } else {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      this._edellinenHakuJson = null
    }
  }

  @HostListener('window:keydown.escape')
  suljeEscapella() {
    if (this.avoinna()) {
      this.sulje()
    }
  }

  sulje() {
    this.suljettu.emit()
  }

  taustaaKlikattu(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.sulje()
    }
  }

  hakutekstiMuuttui() {
    this.sivu = 0
    this._hakuTrigger.next()
  }

  tyhjennaHaku() {
    asetaArvo(this.hakuteksti, '', { ilmoita: false })
    this.hakutekstiMuuttui()
  }

  sivuMuuttui(event: PageEvent) {
    this.sivu = event.pageIndex
    this.sivunKoko = event.pageSize
    this._hakuTrigger.next()
  }

  avaaTuote(tuote: AsiakkaanTuote) {
    this._tuoteDialogiService.avaaTuoterekisterinTuote(tuote)
  }

  private async _suoritaHaku(pyynto: LaskuTuotteetHakuPyynto): Promise<LaskuTuotteetHakuVastaus | null> {
    this.haetaan.set(true)
    try {
      return await this._firebaseLemonaid.functionsCall<LaskuTuotteetHakuPyynto, LaskuTuotteetHakuVastaus>('laskutEtsiTuote', pyynto, undefined, 'europe-north1')
    } catch {
      return null
    }
  }

  private _kasitteleHakuvastaus(vastaus: LaskuTuotteetHakuVastaus | null, pyyntoJson: string) {
    this.haetaan.set(false)
    if (!vastaus || vastaus.e) {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      this._edellinenHakuJson = null
      return
    }
    this._edellinenHakuJson = pyyntoJson
    this.tulokset.set((vastaus.tuotteet ?? []).map(tuote => this._annaNaytettavaHakutulos(tuote)))
    this.kokonaismaara.set(vastaus.kokonaismaara ?? 0)
  }

  private _annaNaytettavaHakutulos(tuote: AsiakkaanTuote): NaytettavaHakutulos {
    const alvTunniste = tuote.alv?.[LaskunTyypit.TAVALLINEN.avain]
    const alv = this._kotimaanAlvt.find(kotimaanAlv => kotimaanAlv.tunniste === alvTunniste)
    const alvProsentti = alv?.prosentti ?? null
    const hintaJaAlvTiedossa = alvProsentti !== null && this._currencyService.onkoNumero(tuote.hinta)
    const hintaVerollinen = hintaJaAlvTiedossa ? this._currencyService.roundHalfUp(tuote.hinta + this._currencyService.annaAlvMaaraAlvittomastaHinnasta(tuote.hinta, alvProsentti), 2) : null
    return { ...tuote, alvProsentti, hintaVerollinen }
  }

  private _rakennaHakupyynto(): LaskuTuotteetHakuPyynto {
    const pyynto: LaskuTuotteetHakuPyynto = { ylaraja: this.sivunKoko, sivu: this.sivu }
    const teksti = this.hakuteksti().value().trim()
    if (teksti) {
      pyynto.vapaahaku = teksti
    }
    return pyynto
  }
}
